import React, { useState } from 'react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import Container from '../../components/Container';
import { ChevronDown, ChevronRight, Shield, Mail, Calendar, Eye, Lock, Clock, Database, Settings, AlertTriangle, Users } from 'lucide-react';

const AccordionItem = ({ title, children, isOpen, onToggle }) => (
  <div className="border border-gray-300 rounded-lg mb-4 bg-white shadow-sm">
    <button
      onClick={onToggle}
      className={`w-full px-4 sm:px-6 py-3 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${isOpen ? 'bg-gray-50' : ''}`}
    >
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 pr-2">{title}</h3>
      {isOpen ? (
        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-[#ab3d22] flex-shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4">
        <div className="text-gray-700 leading-relaxed text-sm sm:text-base">
          {children}
        </div>
      </div>
    )}
  </div>
);

const CalloutBox = ({ children, type = "info" }) => {
  const bgColor = type === "important" ? "bg-[#ab3d22]/10" : "bg-white";
  const borderColor = type === "important" ? "border-[#ab3d22]/30" : "border-gray-300";
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg p-3 sm:p-4 my-4 sm:my-6 shadow-sm`}>
      {children}
    </div>
  );
};

const PrivacyPage = () => {
  const [openSections, setOpenSections] = useState({
    information: true,
    usage: false,
    cookies: false,
    rights: false,
    security: false,
    thirdParty: false,
    future: false,
    changes: false,
    contact: false,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <Navbar />
      
      {/* Editorial Hero Banner */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-[#d6dbd8]"></div>
        
        {/* Subtle Glow Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-slate-200/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-gradient-radial from-zinc-200/20 to-transparent rounded-full blur-3xl"></div>
        </div>
        
        <Container className="relative py-8 sm:py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:min-h-[200px] w-full">
            <div className="text-left flex-1 max-w-lg">
              {/* Main Headline */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 relative" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                Privacy Policy
                {/* Brand Accent Line */}
                <div className="absolute -bottom-2 left-0 w-24 h-1 bg-[#ab3d22] rounded-full"></div>
              </h1>
              
              {/* Refined Tagline */}
              <p className="text-sm md:text-base text-gray-500 leading-relaxed font-light">
                How Radiance collects, uses, and protects your information
              </p>
            </div>
          </div>
        </Container>
      </section>

      <Container className="py-8 sm:py-10">
        <div className="max-w-4xl mx-auto">
          {/* Last Updated */}
          <div className="flex items-center gap-2 mb-8 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>Last Updated: December 8, 2025</span>
          </div>

          {/* Introduction */}
          <div className="mb-8 p-4 bg-white border border-gray-300 rounded-lg shadow-sm">
            <p className="text-sm text-gray-600 mb-4">
              Radiance Inc. ("Radiance," "we," "our," or "us") is committed to respecting your privacy and protecting your personal information. This Privacy Policy explains what data we collect, how we use it, and the choices available to you.
            </p>
            <CalloutBox type="important">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900 mb-1">Your trust matters.</p>
                  <p className="text-sm">
                    We only collect the information necessary to operate Radiance and improve your experience.
                  </p>
                  <p className="text-sm mt-1">
                    We do not sell your personal data.
                  </p>
                </div>
              </div>
            </CalloutBox>
          </div>

          {/* 1. Information We Collect */}
          <AccordionItem
            title="1. Information We Collect"
            isOpen={openSections.information}
            onToggle={() => toggleSection('information')}
          >
            <div className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Email Addresses (Newsletters & Subscriptions)</h4>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• If you subscribe to our newsletter, we collect your email address.</li>
                  <li>• We use it solely to send curated content, updates, and recommendations you've opted into.</li>
                  <li>• You may unsubscribe at any time.</li>
                </ul>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Browsing Activity (Analytics & Cookies)</h4>
                <p className="text-sm text-gray-600">
                  When you visit Radiance, we may collect anonymous data such as:
                </p>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• Pages viewed</li>
                  <li>• Time spent on the site</li>
                  <li>• Device type, browser, and general location</li>
                  <li>• Clicks on certain features or articles</li>
                </ul>
                <p className="text-sm text-gray-600 mt-2">
                  This information helps us understand what readers find useful and how to improve our content and design.
                </p>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Affiliate Link Tracking (Anonymous)</h4>
                <p className="text-sm text-gray-600">When you click an affiliate link:</p>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• An affiliate network may track that a purchase originated from Radiance</li>
                  <li>• This tracking is anonymous and does not identify you personally</li>
                  <li>• We do not receive your name, address, payment details, or any sensitive information</li>
                </ul>
                <p className="text-sm text-gray-600 mt-2">
                  We receive only performance metrics, such as which links or products were popular.
                </p>
              </div>
              
              <CalloutBox>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Information We Do NOT Collect</p>
                    <p className="text-sm mb-2">We do not collect:</p>
                    <ul className="text-sm space-y-1 ml-4">
                      <li>• Health records or medical information</li>
                      <li>• Financial details or payment methods</li>
                      <li>• Government-issued IDs</li>
                      <li>• Sensitive personal data</li>
                    </ul>
                    <p className="text-sm mt-2 font-medium">
                      Radiance is designed to respect your privacy by default.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* 2. How We Use Your Information */}
          <AccordionItem
            title="2. How We Use Your Information"
            isOpen={openSections.usage}
            onToggle={() => toggleSection('usage')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                We use the information we collect to:
              </p>
              
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Provide Email Updates</h4>
                  <p className="text-sm text-gray-600">
                    Only if you've opted in to receive newsletters.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Eye className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Improve Radiance</h4>
                  <p className="text-sm text-gray-600">
                    Analytics help us understand which content is valuable, what users interact with most, and where to improve usability.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Settings className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Personalize Content (Non-Identifiable)</h4>
                  <p className="text-sm text-gray-600">
                    Aggregated browsing patterns may help us surface more relevant content or product categories.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Database className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Measure Affiliate Performance</h4>
                  <p className="text-sm text-gray-600">
                    Anonymous attribution data helps us understand which products resonate most with readers.
                  </p>
                </div>
              </div>
              
              <CalloutBox>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm">
                      We do not use your data to create profiles, sell information, or target ads outside Radiance.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* 3. Cookies & Tracking Technologies */}
          <AccordionItem
            title="3. Cookies & Tracking Technologies"
            isOpen={openSections.cookies}
            onToggle={() => toggleSection('cookies')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance uses cookies to operate effectively and enhance your experience.
              </p>
              
              <h4 className="font-semibold text-gray-900 mt-4">Types of Cookies We Use</h4>
              
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Essential Cookies</h4>
                  <p className="text-sm text-gray-600">Enable basic site functionality</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Analytics Cookies</h4>
                  <p className="text-sm text-gray-600">Help us understand how visitors use Radiance</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Affiliate Cookies</h4>
                  <p className="text-sm text-gray-600">Allow affiliate partners to confirm a purchase originated from our website</p>
                </div>
              </div>
              
              <CalloutBox>
                <div className="flex items-start gap-3">
                  <Settings className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Managing Cookies</p>
                    <p className="text-sm">
                      You can disable cookies in your browser settings.
                    </p>
                    <p className="text-sm mt-1">
                      Please note: certain site features may not function properly if cookies are turned off.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* 4. Your Rights */}
          <AccordionItem
            title="4. Your Rights"
            isOpen={openSections.rights}
            onToggle={() => toggleSection('rights')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Depending on your location (e.g., Canada under PIPEDA, EU/UK under GDPR, California under CCPA), you may have the right to:
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Eye className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Access</h4>
                    <p className="text-sm text-gray-600">Access the personal data we hold about you</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Database className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Deletion</h4>
                    <p className="text-sm text-gray-600">Request deletion of your personal data</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Settings className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Correction</h4>
                    <p className="text-sm text-gray-600">Correct inaccurate information</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Withdraw Consent</h4>
                    <p className="text-sm text-gray-600">Withdraw consent (e.g., unsubscribe from email lists)</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Inquire</h4>
                    <p className="text-sm text-gray-600">Ask how your data is used</p>
                  </div>
                </div>
              </div>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">To exercise these rights, contact us at:</p>
                    <p className="text-sm">
                      📩 <a href="mailto:hello@meetradiance.com" className="text-[#ab3d22] hover:underline">hello@meetradiance.com</a>
                    </p>
                    <p className="text-sm mt-2">
                      We will respond within the time required by applicable law.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* 5. Data Security */}
          <AccordionItem
            title="5. Data Security"
            isOpen={openSections.security}
            onToggle={() => toggleSection('security')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                We use reasonable administrative, technical, and physical safeguards to protect your information.
              </p>
              <p className="text-sm text-gray-600">
                No method of data transmission or storage is completely secure, but we take privacy and security seriously and continuously evaluate best practices.
              </p>
            </div>
          </AccordionItem>

          {/* 6. Third-Party Services */}
          <AccordionItem
            title="6. Third-Party Services"
            isOpen={openSections.thirdParty}
            onToggle={() => toggleSection('thirdParty')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance may rely on trusted third-party providers for:
              </p>
              
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• Newsletter delivery platforms</li>
                <li>• Web analytics tools</li>
                <li>• Affiliate tracking networks</li>
                <li>• Website hosting and infrastructure</li>
              </ul>

              <p className="text-sm text-gray-600">
                These providers may collect information in accordance with their own privacy policies.
              </p>
              <p className="text-sm text-gray-600">
                We encourage you to review their policies directly if you have concerns.
              </p>
            </div>
          </AccordionItem>

          {/* 7. Future Features */}
          <AccordionItem
            title="7. Future Features"
            isOpen={openSections.future}
            onToggle={() => toggleSection('future')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                As Radiance evolves, we may introduce new features such as user accounts or community reviews.
              </p>
              <p className="text-sm text-gray-600">
                If any future changes require additional data collection, we will update this Privacy Policy and notify users when required.
              </p>
            </div>
          </AccordionItem>

          {/* 8. Changes to This Privacy Policy */}
          <AccordionItem
            title="8. Changes to This Privacy Policy"
            isOpen={openSections.changes}
            onToggle={() => toggleSection('changes')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                We may update this Privacy Policy periodically.
              </p>
              <p className="text-sm text-gray-600">
                The "Last Updated" date at the top reflects the most recent revisions.
              </p>
              <p className="text-sm text-gray-600">
                Your continued use of Radiance indicates acceptance of any changes.
              </p>
            </div>
          </AccordionItem>

          {/* 9. Contact Us */}
          <AccordionItem
            title="9. Contact Us"
            isOpen={openSections.contact}
            onToggle={() => toggleSection('contact')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                If you have questions about your privacy or wish to exercise your rights, please contact us:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-[#ab3d22]" />
                    <span className="text-sm font-medium">Email:</span>
                    <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline">
                      hello@meetradiance.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#ab3d22]" />
                    <span className="text-sm font-medium">Typical Response Time:</span>
                    <span className="text-sm text-gray-600">within 48 hours</span>
                  </div>
                </div>
              </div>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm">
                      Your privacy is important to us — and we are committed to handling your information responsibly and transparently.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>
        </div>
      </Container>
      
      <Footer />
    </div>
  );
};

export default PrivacyPage;
